import React, { useState, useEffect } from 'react';
import { Box, Select, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons';
import { ActiveFilter, DateFilterValue, FilterRendererProps } from '../../types';
import { getFixedDateRanges } from '../../../../ideas/filters/FixedDateRanges';

const DateFilterForModal: React.FC<FilterRendererProps> = ({
  filterConfig,
  filterName,
  localActiveFilters,
  setLocalActiveFilters,
  onCountUpdate,
}) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  const filterObject = filterConfig.filterObject || 'evidence';

  useEffect(() => {
    const existingFilter = localActiveFilters.find(
      (filter: any) => filter.name === filterName && filter.filterObject === filterObject
    );

    if (existingFilter?.selected) {
      const dateData = existingFilter.selected as DateFilterValue;
      if (dateData.operator && dateData.operator !== 'custom') {
        setSelectedPreset(dateData.operator);
        if (dateData.values && dateData.values.length === 2) {
          const startDate = new Date(dateData.values[0]);
          const endDate = new Date(dateData.values[1]);
          setDateRange([startDate, endDate]);
        }
      } else if (dateData.values && dateData.values.length === 2) {
        setSelectedPreset('custom');
        const startDate = new Date(dateData.values[0]);
        const endDate = new Date(dateData.values[1]);
        setDateRange([startDate, endDate]);
      }
    } else {
      setDateRange([null, null]);
      setSelectedPreset(null);
    }
  }, [filterName, filterObject, localActiveFilters]);

  const applyWithRange = (start: Date, end: Date, operator: string) => {
    const dateFilterValue: DateFilterValue = {
      operator: operator || 'custom',
      values: [start.toISOString(), end.toISOString()],
    };
    const updatedFilter: ActiveFilter = {
      ...filterConfig,
      name: filterName,
      filterObject: filterObject as 'evidence' | 'idea',
      selected: dateFilterValue,
      query: null,
      options: undefined,
    };
    setLocalActiveFilters((prev: any) => {
      const existingIndex = prev.findIndex(
        (filter: any) => filter.name === filterName && filter.filterObject === filterObject
      );
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = updatedFilter;
        return next;
      }
      return [...prev, updatedFilter];
    });
    onCountUpdate?.(1);
  };

  const removeFilter = () => {
    setLocalActiveFilters((prev: any) =>
      prev.filter((filter: any) => !(filter.name === filterName && filter.filterObject === filterObject))
    );
    onCountUpdate?.(0);
  };

  const handlePresetChange = (preset: string | null) => {
    setSelectedPreset(preset);

    if (preset && preset !== 'custom') {
      const range = getFixedDateRanges[preset];
      if (range?.values?.length === 2 && range.values[0] && range.values[1]) {
        setDateRange(range.values);
        applyWithRange(range.values[0], range.values[1], preset);
      }
    } else if (preset === 'custom') {
      // Keep current date range; apply when user picks both dates
    } else {
      setDateRange([null, null]);
      removeFilter();
    }
  };

  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    if (range[0] && range[1]) {
      applyWithRange(range[0], range[1], selectedPreset || 'custom');
    } else if (range[0] == null && range[1] == null) {
      removeFilter();
    }
  };

  
  const presetOptions = [
    { value: 'thisWeek', label: 'This week' },
    { value: 'lastWeek', label: 'Last week' },
    { value: 'nextWeek', label: 'Next week' },
    { value: 'thisMonth', label: 'This month' },
    { value: 'lastMonth', label: 'Last month' },
    { value: 'nextMonth', label: 'Next month' },
    { value: 'thisQuarter', label: 'This quarter' },
    { value: 'lastQuarter', label: 'Last quarter' },
    { value: 'nextQuarter', label: 'Next quarter' },
    { value: 'thisYear', label: 'This year' },
    { value: 'lastYear', label: 'Last year' },
    { value: 'nextYear', label: 'Next year' },
    { value: 'custom', label: 'Custom range' },
  ];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Stack spacing="sm" sx={{ width: '100%', maxWidth: 280 }}>
        <Select
          placeholder="Select date range"
          value={selectedPreset}
          onChange={handlePresetChange}
          data={presetOptions}
          icon={<IconCalendar size={16} />}
          size="sm"
        />

        <DatePicker
          type="range"
          value={dateRange}
          onChange={handleDateRangeChange}
          placeholder="Select date range"
          size="sm"
        />
      </Stack>
    </Box>
  );
};

export default DateFilterForModal;
